import React from 'react'
import { motion } from 'framer-motion'
import { Github, Linkedin, Mail, Globe, MapPin } from 'lucide-react'
import { Profile, Social } from '../types'
import { fadeUp, staggerContainer, wiggle } from '../lib/motion'

interface HeroProps {
  data: Profile
  socials: Social[]
}

const Hero: React.FC<HeroProps> = ({ data, socials }) => {
  const getIcon = (iconName: string) => {
    switch (iconName) {
      case 'github':
        return <Github className="h-5 w-5" />
      case 'linkedin':
        return <Linkedin className="h-5 w-5" />
      case 'mail':
        return <Mail className="h-5 w-5" />
      case 'globe':
        return <Globe className="h-5 w-5" />
      default:
        return <Globe className="h-5 w-5" />
    }
  }

  const scrollToContact = () => {
    const element = document.getElementById('contact')
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  return (
    <section id="hero" className="min-h-screen flex items-center justify-center bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <motion.div
        className="container-max section-padding py-20"
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
      >
        <div className="text-center">
          {/* Profile Image */}
          <motion.div className="mb-6 sm:mb-8" variants={fadeUp}>
            <div className="w-24 h-24 sm:w-32 sm:h-32 mx-auto rounded-full bg-gradient-to-r from-primary-500 to-accent-500 p-1">
              <div className="w-full h-full rounded-full overflow-hidden">
                <img
                  src="https://r2.adityaariizkyy.com/DSCF9335_KALIKI%20STUDIO%203x4.jpg"
                  alt={data.name}
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
          </motion.div>

          {/* Name and Title */}
          <motion.h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4 px-4" variants={fadeUp}>
            {data.name}
          </motion.h1>
          <motion.h2 className="text-base sm:text-lg md:text-xl lg:text-2xl text-gradient font-semibold mb-4 sm:mb-6 px-4" variants={fadeUp}>
            {data.title}
          </motion.h2>

          {/* Hook tagline */}
          <motion.p className="text-sm sm:text-base md:text-lg font-medium text-gray-800 dark:text-gray-200 mb-4 sm:mb-6 px-4" variants={fadeUp}>
            I keep banking systems up when the traffic doesn't.
          </motion.p>

          {/* Location */}
          <motion.div className="flex items-center justify-center text-gray-600 dark:text-gray-400 mb-6 sm:mb-8 px-4" variants={fadeUp}>
            <MapPin className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            <span className="text-sm sm:text-base">{data.location}</span>
          </motion.div>

          {/* Summary */}
          <motion.p className="text-sm sm:text-base lg:text-lg text-gray-700 dark:text-gray-300 max-w-3xl mx-auto mb-6 sm:mb-8 leading-relaxed px-4" variants={fadeUp}>
            {data.summary}
          </motion.p>

          {/* Social Links */}
          <motion.div className="flex items-center justify-center space-x-3 sm:space-x-4 mb-6 sm:mb-8" variants={fadeUp}>
            {socials.map((social, index) => (
              <motion.a
                key={index}
                href={social.url}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 sm:p-3 bg-white dark:bg-gray-800 rounded-full shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-200 dark:border-gray-700 group"
                aria-label={social.name}
                whileHover={{ scale: 1.12, ...wiggle.whileHover }}
                whileTap={{ scale: 0.95 }}
                transition={wiggle.transition}
              >
                <div className="text-gray-600 dark:text-gray-400 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors">
                  {getIcon(social.icon)}
                </div>
              </motion.a>
            ))}
          </motion.div>

          {/* CTA Buttons */}
          <motion.div className="flex flex-col sm:flex-row items-center justify-center space-y-3 sm:space-y-0 sm:space-x-4 px-4" variants={fadeUp}>
            <motion.button
              onClick={scrollToContact}
              className="btn-primary px-6 sm:px-8 py-2 sm:py-3 text-base sm:text-lg font-semibold rounded-full w-full sm:w-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              Get In Touch
            </motion.button>
            <motion.a
              href="#projects"
              className="btn-secondary px-6 sm:px-8 py-2 sm:py-3 text-base sm:text-lg font-semibold rounded-full inline-flex items-center justify-center w-full sm:w-auto"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              View My Work
            </motion.a>
          </motion.div>
        </div>
      </motion.div>
    </section>
  )
}

export default Hero
